import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Target,
  Compass,
  Handshake,
  ShieldCheck,
  Zap,
  MapPinned,
  CheckCircle2,
} from "lucide-react";
import PageShell from "@/components/site/PageShell";
import useSEO from "@/lib/useSEO";

const REASONS = [
  {
    i: Target,
    t: "Specialized focus",
    d: "We work exclusively on Cost Segregation and R&D Tax Credits — no distractions, no upsells, no generic consulting.",
  },
  {
    i: ShieldCheck,
    t: "IRS-compliant studies",
    d: "Every engagement is backed by experienced engineers and tax professionals, with documentation built to withstand audit.",
  },
  {
    i: Handshake,
    t: "Collaborative with your CPA",
    d: "We work alongside your tax advisor — sharing methodology, figures, and form-ready deliverables on your timeline.",
  },
  {
    i: Compass,
    t: "Transparent communication",
    d: "Clear scope, clear deliverables, clear pricing. You'll always know where your study stands.",
  },
  {
    i: Zap,
    t: "Fast turnaround",
    d: "Personalized service that doesn't sacrifice speed. We move quickly because your filing deadlines do too.",
  },
  {
    i: MapPinned,
    t: "Nationwide service",
    d: "We serve clients across the U.S. while applying local market and state-specific insight to every study.",
  },
];

export default function About() {
  useSEO({
    title: "About IncentTax | Cost Segregation & R&D Tax Credit Experts",
    description:
      "IncentTax helps businesses, property owners, and investors uncover tax savings through specialized cost segregation studies and R&D tax credit services. Nationwide service, personalized approach.",
    canonical: "https://incenttax.com/about",
  });
  return (
    <PageShell testid="about-page">
      {/* HERO */}
      <section className="bg-forest-900 text-ivory">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 py-24 sm:py-32">
          <p className="text-brass uppercase tracking-[0.28em] text-sm mb-6">About IncentTax</p>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.04] tracking-tight">
            Turning complex tax strategies
            <br />
            <span className="italic text-brass">into measurable savings.</span>
          </h1>
          <p className="mt-8 text-ivory/85 text-lg sm:text-xl leading-relaxed max-w-3xl">
            At IncentTax, we help businesses, real estate investors, and
            property owners uncover valuable tax savings through specialized
            Cost Segregation Studies and Research &amp; Development (R&amp;D)
            Tax Credit services. Our mission is simple: transform complex tax
            regulations into practical financial opportunities that improve
            cash flow and support long-term growth.
          </p>
        </div>
      </section>

      {/* MISSION / WHO WE SERVE */}
      <section className="py-24 bg-ivory">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="text-forest-700 uppercase tracking-[0.24em] text-sm mb-4">
              Who we serve
            </p>
            <h2 className="font-serif text-4xl sm:text-5xl text-forest-900 leading-tight">
              CPAs, owners, and investors —{" "}
              <span className="italic text-brass">coast to coast.</span>
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-5 text-charcoal text-lg leading-relaxed">
            <p>
              We work alongside <strong>CPAs, tax professionals, commercial real estate owners,</strong>{" "}
              and <strong>businesses across the United States</strong>, providing expert
              guidance throughout every stage of the process.
            </p>
            <p>
              Whether you're purchasing, constructing, renovating, or improving
              a property — or investing in innovation through research and
              development — we help identify every opportunity available under
              current tax law.
            </p>
          </div>
        </div>
      </section>

      {/* NATIONAL EXPERTISE / PERSONALIZED SERVICE */}
      <section className="py-24 bg-ivory-200">
        <div className="max-w-7xl mx-auto px-6 sm:px-8">
          <p className="text-forest-700 uppercase tracking-[0.24em] text-sm mb-4">
            How we work
          </p>
          <h2 className="font-serif text-4xl sm:text-5xl text-forest-900 mb-10 max-w-4xl leading-tight">
            National expertise.{" "}
            <span className="italic text-brass">Personalized service.</span>
          </h2>
          <div className="grid md:grid-cols-2 gap-px bg-ivory-400 border border-ivory-400">
            <div className="bg-white p-10">
              <div className="font-serif text-2xl text-forest-900 mb-4">
                Not one-size-fits-all
              </div>
              <p className="text-charcoal leading-relaxed">
                Although we serve clients nationwide, our approach is anything
                but generic. Every project is evaluated based on your unique
                business, property, and tax situation. We combine federal tax
                expertise with an understanding of state-specific opportunities
                to deliver customized strategies that maximize your financial
                benefit.
              </p>
            </div>
            <div className="bg-white p-10">
              <div className="font-serif text-2xl text-forest-900 mb-4">
                A deep professional network
              </div>
              <p className="text-charcoal leading-relaxed">
                Through our extensive network of{" "}
                <strong>licensed engineers, tax specialists, and valuation
                professionals,</strong>{" "}
                we deliver IRS-compliant studies and documentation that give
                you and your CPA confidence — while minimizing disruption to
                your business.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="py-24 bg-forest-900 text-ivory">
        <div className="max-w-7xl mx-auto px-6 sm:px-8">
          <p className="text-brass uppercase tracking-[0.24em] text-sm mb-4">
            Why businesses choose IncentTax
          </p>
          <h2 className="font-serif text-4xl sm:text-5xl text-ivory mb-12 max-w-4xl leading-tight">
            Six reasons we're <span className="italic text-brass">the right call.</span>
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-ivory/10 border border-ivory/10">
            {REASONS.map(({ i: Icon, t, d }) => (
              <div key={t} className="bg-forest-900 p-10">
                <Icon className="text-brass mb-5" size={28} strokeWidth={1.5} />
                <div className="font-serif text-2xl text-ivory mb-3">{t}</div>
                <p className="text-ivory/80 leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COMMITMENT */}
      <section className="py-24 bg-ivory">
        <div className="max-w-5xl mx-auto px-6 sm:px-8 text-center">
          <CheckCircle2 className="text-brass mx-auto mb-6" size={40} strokeWidth={1.5} />
          <h2 className="font-serif text-4xl sm:text-5xl text-forest-900 leading-tight mb-6">
            Our commitment.{" "}
            <span className="italic text-brass">Long-term relationships.</span>
          </h2>
          <p className="text-charcoal text-lg leading-relaxed">
            At IncentTax, we're committed to building long-term relationships
            by delivering accurate studies, exceptional service, and
            measurable tax savings — helping our clients keep more of what
            they've earned.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-forest-950 text-ivory">
        <div className="max-w-5xl mx-auto px-6 sm:px-8 text-center">
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-ivory leading-tight mb-6">
            Ready to find your savings?{" "}
            <span className="italic text-brass">Let's talk.</span>
          </h2>
          <p className="text-ivory/80 text-lg mb-10 max-w-2xl mx-auto">
            A 30-minute consultation tells you whether a study makes sense —
            and roughly how much cash you could recover. No engagement until
            we confirm the opportunity.
          </p>
          <Link
            to="/#contact"
            data-testid="about-bottom-cta"
            className="inline-flex items-center justify-center gap-2 bg-brass hover:bg-brass-500 text-forest-900 px-10 py-4 font-medium"
          >
            Schedule a free consultation <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </PageShell>
  );
}
